/**
 * The words for the Re-send control (card #643 follow-on), in ONE place.
 *
 * `POST /batch-management/orders/<id>/resend` answers with an allow-listed
 * `result` token (`order_resend.RESEND_RESULT_TOKENS`). The token is OUR
 * vocabulary, never partner text, but it is still a code, not a sentence:
 * this map is the only place that turns one into words. An unknown token
 * (the backend gained one this map has not) reads as the generic sentence,
 * never as the raw token.
 *
 * Every refusal sentence says what happened to the ORDER first. Where the
 * server refused before any partner call, or the send was refused outright,
 * it says "Nothing was sent" so the distributor is never left guessing
 * whether a double send is possible.
 *
 * Awaiting Jake's copy review (see the copy-review table for this change).
 */

export const RESEND_GENERIC_SENTENCE =
  "We could not re-send this order just now. Try again in a moment, " +
  "and contact support if it keeps happening.";

export const RESEND_SENTENCES = Object.freeze({
  resend_order_not_found: "We could not find that order.",
  resend_no_back_office_order:
    "This order has not reached the decorator, so there is nothing to re-send.",
  resend_identity_unconfirmed:
    "The decorator has not confirmed this order yet, so it cannot be " +
    "re-sent safely. Nothing was sent. Try again later.",
  resend_not_batched: "This order is not in a batch, so it cannot be re-sent here.",
  resend_batch_closed:
    "The decorator has already closed this batch, so this order can no " +
    "longer be re-sent. Nothing was sent.",
  resend_not_submitted:
    "This order was not sent to the decorator in this batch, or it was " +
    "cancelled, so it cannot be re-sent. Nothing was sent.",
  resend_in_progress:
    "A re-send for this order is already running. Wait a moment, then check again.",
  resend_batch_busy:
    "The batch is busy right now. Nothing was sent. Try again in a moment.",
  resend_batch_state_unavailable:
    "We could not check the batch with the decorator just now. Nothing was " +
    "sent. Try again in a moment.",
  resend_batch_not_drain_released:
    "This batch was released in a way that does not allow a re-send from " +
    "here. Nothing was sent.",
  design_missing:
    "This order still has a logo with no design at the decorator, so it was " +
    "not re-sent. Fix the artwork first.",
  design_check_unavailable:
    "We could not check this order's artwork just now. Nothing was sent. " +
    "Try again in a moment.",
  design_publish_failed:
    "The logo could not be sent to the decorator just now. Nothing was " +
    "re-sent. Try again in a moment.",
  design_publish_refused:
    "The artwork needs a fix before it can be sent to the decorator. " +
    "Nothing was re-sent.",
  resend_contract_violation:
    "This order cannot be re-sent as it stands, so nothing was sent. " +
    "Contact support if this keeps happening.",
  resend_stub_mode:
    "The decorator connection is in test mode here, so nothing was sent.",
  resend_not_found:
    "The decorator could not find this order. Nothing was changed.",
  resend_order_locked:
    "The decorator has already started work on this order, so it cannot be changed.",
  resend_rejected: "The decorator refused the re-send. Nothing was changed.",
  resend_rate_limited:
    "The decorator asked us to slow down. Try again in a minute.",
  resend_unavailable:
    "The decorator is not answering right now. Try again in a moment.",
  resend_identity_mismatch:
    "The decorator's answer did not match this order, so we cannot tell " +
    "whether it was changed. Do not try again. Contact support.",
  resend_decorator_not_honored:
    "The order was re-sent, but the decorator did not keep the decorator " +
    "choice on it. Contact support to check the order.",
});

/**
 * A confirmed send whose answer never came back (network failure, timeout,
 * a 5xx with no outcome body): the server may have sent it. Never say "try
 * again" plain here.
 */
export const RESEND_UNCERTAIN_SENTENCE =
  "We could not confirm whether this order was re-sent. It may have been " +
  "sent. Refresh the list and check the batch before trying again.";

/** HTTP statuses that carry no result token (permission, scope, rate limit). */
export const RESEND_STATUS_SENTENCES = Object.freeze({
  403: "You do not have permission to re-send this order.",
  404: "We could not find that order in your stores.",
  429: "Too many requests. Wait a minute and try again.",
});

export function resendSentence(token) {
  return Object.hasOwn(RESEND_SENTENCES, token)
    ? RESEND_SENTENCES[token]
    : RESEND_GENERIC_SENTENCE;
}

/** The sentence for a request that failed without an answer body. */
export function resendStatusSentence(status) {
  return Object.hasOwn(RESEND_STATUS_SENTENCES, status)
    ? RESEND_STATUS_SENTENCES[status]
    : RESEND_GENERIC_SENTENCE;
}

/**
 * Batch states in which a re-send can work: the release reached the close and
 * the close has not succeeded. Mirrors `order_resend.RESENDABLE_BATCH_STATES`;
 * the server still decides (and refuses with `resend_batch_closed`).
 */
export const RESENDABLE_BATCH_STATUSES = Object.freeze(["releasing", "release_blocked"]);
